import Close from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

interface ToastItem {
  id: string | number;
  status: 'added' | 'updated' | 'removed';
}

const CLEAR_ALL_VISIBLE_CLASS = 'clear-all-notifications-visible';

const ClearAllNotifications: React.FC = () => {
  const [toastCount, setToastCount] = useState(0);

  useEffect(() => {
    const toastSet = new Set<string | number>();

    const unsubscribe = toast.onChange((payload: ToastItem) => {
      if (payload.status === 'added') {
        toastSet.add(payload.id);
      } else if (payload.status === 'removed') {
        toastSet.delete(payload.id);
      }
      setToastCount(toastSet.size);
    });

    return () => unsubscribe();
  }, []);

  const isVisible = toastCount >= 2;

  useEffect(() => {
    if (isVisible) {
      document.body.classList.add(CLEAR_ALL_VISIBLE_CLASS);
    } else {
      document.body.classList.remove(CLEAR_ALL_VISIBLE_CLASS);
    }

    return () => {
      document.body.classList.remove(CLEAR_ALL_VISIBLE_CLASS);
    };
  }, [isVisible]);

  const handleClearAll = useCallback(() => {
    toast.dismiss();
    setToastCount(0);
  }, []);

  if (!isVisible) {
    return null;
  }

  return (
    <Box
      component="button"
      type="button"
      sx={(theme) => ({
        position: 'fixed',
        top: '12px',
        right: '12px',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        backgroundColor: theme.palette.brandV2Black.main,
        color: theme.palette.brandV2Black.contrastText,
        border: 'none',
        borderRadius: '6px',
        padding: '10px 12px',
        boxShadow:
          '0px 3px 5px -1px rgba(0,0,0,0.2), 0px 6px 10px 0px rgba(0,0,0,0.14), 0px 1px 18px 0px rgba(0,0,0,0.12)',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: theme.palette.brandV2Black.light,
        },
      })}
      onClick={handleClearAll}
      aria-label={t('common.clear_all_notifications')}
    >
      <IconButton
        size="small"
        sx={(theme) => ({
          color: theme.palette.brandV2Black.contrastText,
          padding: '2px',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          },
        })}
      >
        <Close sx={{ fontSize: '18px' }} />
      </IconButton>
      <Typography
        sx={{
          fontSize: '14px',
          fontWeight: 500,
          whiteSpace: 'nowrap',
        }}
      >
        {t('common.clear_all_notifications')}
      </Typography>
    </Box>
  );
};

export default ClearAllNotifications;
